import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectActiveMatter } from '@/matter/store/selectors'
import { selectIsUserFilesOperationActive } from '@/user_files/batching/store/selectors'
import { selectActiveMatterExternalSyncMapping } from '@/user_files/external-storage/store/slice'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { selectCurrentFolderId } from '@/user_files/folders/store/selectors'
import { getSearchErrorMessage } from '../search-error'
import { searchMatterFiles } from '../fetch/search-matter-files'
import { searchMatterFolders } from '../fetch/search-matter-folders'
import { buildSearchRows, type DriveSearchRow } from '../search-rows'
import type { FileSearchResult, FolderSearchResult } from '../schemas'
import { isAbortError } from '@/util/errors/is-abort-error'

export type MatterFilesSearchStatus = 'loading' | 'success' | 'error'

type SearchResults = {
  folders: FolderSearchResult[]
  files: FileSearchResult[]
  /** Backend match totals, which exceed the fetched rows only when the page cap was hit. */
  totalCount: number
  truncated: boolean
}

const EMPTY_RESULTS: SearchResults = { folders: [], files: [], totalCount: 0, truncated: false }

export type MatterFilesSearch = {
  /** Current input value. */
  query: string
  setQuery: (value: string) => void
  /** The query the results belong to; `null` while browsing. */
  submittedQuery: string | null
  /** Runs a search. Defaults to the current input value; empty input clears back to browse. */
  submitQuery: (value?: string) => void
  clearSearch: () => void
  retry: () => void
  /** True while the table shows results instead of the current folder. */
  isActive: boolean
  status: MatterFilesSearchStatus
  error: string | null
  /** Folders first, then files, each in the order the backend ranked them. */
  rows: DriveSearchRow[]
  /** True when the page cap stopped pagination, so `rows` is a prefix of the matches. */
  truncated: boolean
  /** Total matches the backend counted, used only to explain a truncated result set. */
  totalCount: number
  /** Ancestor folder ids per result id, for `pruneCoveredSelection`. */
  ancestorFolderIdsById: ReadonlyMap<string, readonly string[]>
}

/**
 * Matter-wide file and folder search for the drive.
 *
 * Search is committed on Enter rather than per keystroke: the results table replaces the folder
 * listing, so it should change when the user asks for it. Both endpoints are matter-scoped, so a
 * result can live anywhere in the matter — every row carries the Location it came from, and the
 * ancestry needed to keep a cross-folder selection safe to batch.
 */
export function useMatterFilesSearch(): MatterFilesSearch {
  const dispatch = useAppDispatch()
  const activeMatter = useAppSelector(selectActiveMatter)
  const currentFolderId = useAppSelector(selectCurrentFolderId)
  const isOperationActive = useAppSelector(selectIsUserFilesOperationActive)
  // Read, never fetched here: the drive's source view already loads the mapping.
  const syncMapping = useAppSelector((state) => selectActiveMatterExternalSyncMapping(state, activeMatter?.id))

  const [query, setQuery] = useState('')
  const [submittedQuery, setSubmittedQuery] = useState<string | null>(null)
  const [status, setStatus] = useState<MatterFilesSearchStatus>('success')
  const [error, setError] = useState<string | null>(null)
  const [results, setResults] = useState<SearchResults>(EMPTY_RESULTS)
  const [refreshToken, setRefreshToken] = useState(0)

  const activeMatterId = activeMatter?.id ?? null

  const leaveResults = useCallback(() => {
    setSubmittedQuery(null)
    setResults(EMPTY_RESULTS)
    setError(null)
    setStatus('success')
  }, [])

  const clearSearch = useCallback(() => {
    setQuery('')
    leaveResults()
  }, [leaveResults])

  // Emptying the field — including via the clear button — returns to the folder listing, so the
  // table never shows results for a query the input no longer holds.
  const handleQueryChange = useCallback(
    (value: string) => {
      setQuery(value)
      if (!value.trim()) leaveResults()
    },
    [leaveResults]
  )

  const submitQuery = useCallback(
    (value?: string) => {
      const trimmed = (value ?? query).trim()
      if (!trimmed) {
        clearSearch()
        return
      }

      // Running a recent search puts it back in the field, so the input keeps describing the table.
      if (value !== undefined) setQuery(value)
      // Enter the loading state in the same render as the query, so the table never paints one frame
      // of "no results" for a search that has not run yet.
      setStatus('loading')
      setResults(EMPTY_RESULTS)
      setSubmittedQuery(trimmed)
      // Re-running the search already on screen leaves `submittedQuery` untouched, so nothing the
      // fetch effect depends on moves and the loading state above would never be cleared. The
      // refresh token is the one dep that changes on every call, so it is what re-runs the fetch.
      if (trimmed === submittedQuery) setRefreshToken((token) => token + 1)
    },
    [query, submittedQuery, clearSearch]
  )

  const retry = useCallback(() => setRefreshToken((token) => token + 1), [])

  // Navigating — including by clicking a folder result — leaves search, so the breadcrumb the
  // user lands on describes what they are looking at.
  useEffect(() => {
    clearSearch()
  }, [currentFolderId, clearSearch])

  useEffect(() => {
    if (!activeMatterId || !submittedQuery) return

    const controller = new AbortController()
    let cancelled = false

    setStatus('loading')
    setError(null)

    Promise.all([
      searchMatterFolders(activeMatterId, { query: submittedQuery, signal: controller.signal }),
      searchMatterFiles(activeMatterId, { query: submittedQuery, signal: controller.signal }),
    ])
      .then(([folderResults, fileResults]) => {
        if (cancelled) return

        // Results are records the rest of the drive may not have loaded — a move out of a folder
        // the user never opened still needs its name for conflict checks and preconditions.
        dispatch(UserFoldersActions.upsertFolders(folderResults.folders))
        dispatch(UserFilesActions.upsertUserFiles(fileResults.files))

        setResults({
          folders: folderResults.folders,
          files: fileResults.files,
          totalCount: folderResults.totalCount + fileResults.totalCount,
          truncated: folderResults.truncated || fileResults.truncated,
        })
        setStatus('success')
      })
      .catch((cause) => {
        if (cancelled || isAbortError(cause)) return
        setResults(EMPTY_RESULTS)
        setError(getSearchErrorMessage(cause))
        setStatus('error')
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [activeMatterId, submittedQuery, refreshToken, dispatch])

  // A move or delete launched from results can touch folders the user is not browsing, and the
  // batching listener only refreshes the current and destination folders. Re-run the search and
  // mark the affected parents stale so their next visit refetches.
  const wasOperationActive = useRef(false)
  useEffect(() => {
    if (wasOperationActive.current && !isOperationActive && submittedQuery) {
      // Both halves of the result set, since either can be acted on: a folder's own parent loses a
      // row, and a file's `folder_id` is the only place its real parent is named — the operation
      // carries the folder the user happens to be browsing, not the one the row came from.
      const affectedParentIds = new Set(
        [...results.folders.map((folder) => folder.parent_id), ...results.files.map((file) => file.folder_id)].filter((folderId): folderId is string =>
          Boolean(folderId)
        )
      )
      affectedParentIds.forEach((folderId) => dispatch(UserFoldersActions.invalidateFolderChildren({ folderId })))
      setRefreshToken((token) => token + 1)
    }
    wasOperationActive.current = isOperationActive
  }, [isOperationActive, submittedQuery, results.folders, results.files, dispatch])

  const { rows, ancestorFolderIdsById } = useMemo(() => {
    // Results deliberately span both sources. The two browse views are split by the source toggle —
    // Paxton's own files on one side, the synced DMS subtree on the other — but search is the
    // "find anything in this matter" affordance, so a DMS hit belongs here exactly like any other.
    return buildSearchRows({
      folders: results.folders,
      files: results.files,
      matterName: activeMatter?.name ?? 'Matter',
      hasSyncedDrive: Boolean(syncMapping),
    })
  }, [results.folders, results.files, activeMatter?.name, syncMapping])

  return {
    query,
    setQuery: handleQueryChange,
    submittedQuery,
    submitQuery,
    clearSearch,
    retry,
    isActive: submittedQuery !== null,
    status,
    error,
    rows,
    truncated: results.truncated,
    totalCount: results.totalCount,
    ancestorFolderIdsById,
  }
}
